import React, { useState, useEffect, useMemo, useCallback } from "react";
import { withRouter } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import { client } from "../../../../index";
import { GET_LINKABLE_REGISTRATION_NUMBER_PAGINATED_DATA } from "../../../../screens/Cases/queries";
import FormTooltip from "../../../SimpleForm/UI/FormTooltip";
import getColumns from "../../getColumns";
import { ReactTable } from "../../../SimpleForm/ReactTable";
import { useDebounce } from "../../../Hooks";

const TableScreen = ({
  formId,
  schema,
  allowOutOfNetwork,
  selectedRegistrationNumber,
  setSelectedRegistrationNumber,
  setModalResetFunc,
  resetAndClose,
  history,
  sortingValues,
}) => {
  const userData = useSelector((state) => state.appReducer.userDataReducer.data);
  const userId = userData.subject;
  const groupRole = userData.GroupRole;
  const groupId = userData.Group.id;
  const [data, setData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [isOutOfNetwork, setIsOutOfNetwork] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageCount, setPageCount] = useState(0);

  const debouncedSearchTerm = useDebounce(searchTerm);

  useEffect(() => {
    setModalResetFunc(() => () => {
      setData([]);
      setDataLoading(false);
      setIsOutOfNetwork(false);
      setSearchTerm("");
      setPageCount(0);
    });
  }, []);

  const radioColumn = useMemo(() => [
    {
      id: "radio",
      accessor: "",
      Cell: ({ row: { original } }) => {
        return (
          <>
            <input
              type="radio"
              name="pwd-selection"
              className="usa-radio-input"
              id={`pwd-${original.registrationNumber}`}
              onChange={(e) => e}
              checked={
                selectedRegistrationNumber?.registrationNumber ===
                original.registrationNumber
              }
              onClick={() => setSelectedRegistrationNumber(original)}
            />
            <label
              className="usa-radio-label"
              htmlFor={`pwd-${original.registrationNumber}`}
              onChange={(e) => e}
              onClick={() => setSelectedRegistrationNumber(original)}
              checked={
                selectedRegistrationNumber?.registrationNumber ===
                original.registrationNumber
              }
            />
          </>
        );
      },
      disableSortBy: true,
      fixedWidth: 25,
    },
  ]);

  const columns = useMemo(() => getColumns(), []);

  const {
    title,
    headerInNetwork,
    headerOutNetwork,
    queryCriteriaInNetwork,
    queryCriteriaOutNetwork,
    searchTermText,
    searchTermTextOutOfNetwork,
  } = schema;
  const header = !isOutOfNetwork ? headerInNetwork : headerOutNetwork;
  const tooltip = !isOutOfNetwork
    ? queryCriteriaInNetwork
    : queryCriteriaOutNetwork;

  const application_id = history.location.pathname.split("/")[4];

  const queryParamsBuilder = ({ pageSize, pageIndex, sortBy, searchTerm }) => {
    const variables = {
      UISorting: {
        type: "LinkableRegistrationNumber",
        form_id: formId,
        is_out_of_network: isOutOfNetwork,
        limit: 10,
        // Different from pw linking because graphQL smelly page++ in case.pagination.ts
        page: pageIndex,
        sort_ascending: sortBy[0].desc ? 1 : 0,
        sort_column: sortBy[0].id,
        temp_need: sortingValues.tempneedNature || "",
        temp_workers: sortingValues.tempneedWkrPos || "",
        temp_start: sortingValues.tempneedStart || "",
        temp_end: sortingValues.tempneedEnd || "",
      },
      UISearch: {
        search: searchTerm,
      },
      getCasesInput: {
        user_id: userId,
        groupRole: groupRole,
        group_id: groupId,
        application_id,
      },
    };
    return variables;
  };

  const getLinkableRegistrationNumbers = useCallback(
    async ({ pageSize, pageIndex, sortBy, searchTerm }) => {
      setDataLoading(true);
      const queryParams = queryParamsBuilder({
        pageSize,
        pageIndex,
        sortBy,
        searchTerm,
      });
      client
        .query({
          query: GET_LINKABLE_REGISTRATION_NUMBER_PAGINATED_DATA,
          variables: queryParams,
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          const { data, loading, error } = response;

          if (!loading && !!data) {
            const dataTotalCount = data.getPaginatedData.total_count;
            setData(data.getPaginatedData.RegistrationNumberCases);
            setPageCount(Math.ceil(dataTotalCount / pageSize));
            setDataLoading(false);
          }
        });
    },
    [debouncedSearchTerm, isOutOfNetwork]
  );

  const toggleOutOfNetwork = () => {
    document.getElementById("linking-search").value = "";
    resetAndClose(false);
    setIsOutOfNetwork(!isOutOfNetwork);
  };

  return (
    <>
      <h3>{title}</h3>
      <p className="pwd-description">
        {!!header && header}
        {!!tooltip && <FormTooltip content={tooltip} />}
        <br />
        <i>{isOutOfNetwork ? searchTermTextOutOfNetwork : searchTermText}</i>
      </p>
      <div className="pwd-search-row">
        <div
          className="usa-search usa-search-big margin-top-2"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div role="search">
            <input
              type={"text"}
              className={"usa-input pwd-input"}
              placeholder={"Type search term here..."}
              id={"linking-search"}
              onChange={(e) => {
                setSearchTerm(e.target.value.toUpperCase());
              }}
            />
            <button
              type="button"
              className={"usa-button usa-button-primary btn-pwd-search"}
              style={{
                marginLeft: "1rem",
              }}
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              Search
            </button>
          </div>

          {!!allowOutOfNetwork && (
            <span
              className={`pwd-modal-switch-link ${dataLoading && "disabled"}`}
              onClick={toggleOutOfNetwork}
            >
              {!isOutOfNetwork
                ? "Look up Registration Number Out-of-Network"
                : "Look up Registration Number In-Network"}
            </span>
          )}
        </div>
      </div>
      <ReactTable
        loading={dataLoading}
        data={data}
        fetchData={getLinkableRegistrationNumbers}
        pageCount={pageCount}
        searchTerm={debouncedSearchTerm}
        defaultPageSize={!isOutOfNetwork ? 10 : 1}
        tableStyle={{ marginTop: 25 }}
        refetchToFirstPageDependencies={[isOutOfNetwork]}
        striped
        defaultSortBy={[
          {
            id: "validityStartDate",
            desc: false,
          },
        ]}
        showPagination={!isOutOfNetwork}
        columns={[...radioColumn, ...columns]}
      />
    </>
  );
};

export default withRouter(TableScreen);
